import { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { phoneWithInternationalCode } from '../utils/helpers/common';
import { sendAnalyticsDataMutation } from '../api/mutations';
import useScreenSize from './useScreenSize';
import {
  AnalyticLogEventTypeEnum,
  AnalyticLogTargetTypeEnum
} from '../graphql/types';
import { useAnalyticLogSource } from './useAnalyticLogSource';
import { CreateAnalyticLogMutation } from '../graphql/graphql';

interface UseFullPhoneNumbersProps {
  id?: string | string[];
  residenceId: string | null;
  path: string;
  targetType: AnalyticLogTargetTypeEnum;
  agencyId: string | null;
  setErrorUI?: (value: boolean) => void;
}

export enum PhoneButtonTargetTypeEnum {
  gallery = 'gallery',
  main = 'main',
  contact = 'contact'
}

export const useFullPhoneNumbers = ({
  id,
  residenceId,
  path,
  targetType,
  agencyId,
  setErrorUI
}: UseFullPhoneNumbersProps) => {
  const client = useApolloClient();
  const analyticLogSource = useAnalyticLogSource();

  const [phonesShown, setPhonesShown] = useState<string[] | null>(null);
  const [showAlertMessage, setShowAlertMessage] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const { isSmall, isSmallDesktop, isLarge } = useScreenSize();

  const isCurrentItemPage = targetType === 'ITEM';
  const isAgencyPage = targetType === 'AGENCY';
  const isResidencePage = targetType === 'RESIDENCE';
  let targetId;

  switch (true) {
    case isAgencyPage:
      targetId = agencyId;
      break;
    case isResidencePage:
      targetId = residenceId;
      break;
    default:
      targetId = id;
  }

  const callPhones = (
    phones: string[] | undefined = [],
    index: number,
    triggerButton: PhoneButtonTargetTypeEnum | null,
    isCalled: boolean
  ) => {
    if (
      (isResidencePage && isCalled) ||
      (triggerButton === PhoneButtonTargetTypeEnum.gallery && !isSmall) ||
      (isCurrentItemPage && (isLarge || isSmallDesktop))
    ) {
      return;
    }

    if (
      (phones?.length || 0) > 1 &&
      triggerButton !== PhoneButtonTargetTypeEnum.contact
    ) {
      setIsVisible(true);
    } else {
      window.open(`tel:${phoneWithInternationalCode(phones?.[index])}`, '_top');
    }
  };

  const sendAnalyticsData = (
    triggerButton: PhoneButtonTargetTypeEnum | null,
    phones: string[] | undefined
  ) => {
    try {
      client.mutate<CreateAnalyticLogMutation>({
        mutation: sendAnalyticsDataMutation,
        variables: {
          targetId,
          targetType,
          sourceLink: `${window.location?.origin}${window.location?.pathname}`,
          triggerButton,
          phones,
          eventType: AnalyticLogEventTypeEnum.ShowPhones,
          source: analyticLogSource
        },
        fetchPolicy: 'network-only'
      });
    } catch (error) {
      setErrorUI?.(true);
      console.log(`Error occurred: ${error}`);
    }
  };

  const fetchData = async (
    index = 0,
    isCalled: boolean,
    triggerButton: PhoneButtonTargetTypeEnum | null
  ) => {
    try {
      const response = await fetch(
        `${window.location?.origin}/${path}/${id}/phones?react=true`,
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      if (!response.ok) {
        setErrorUI?.(true);
        return;
      }

      const data: { phones?: string[] } = await response.json();

      if (data?.phones && data.phones.length) {
        callPhones(data.phones, index, triggerButton, isCalled);
        sendAnalyticsData(triggerButton, data.phones);
        setPhonesShown(data.phones);
      } else {
        setShowAlertMessage(true);
      }
    } catch (error) {
      setErrorUI?.(true);
      console.log(`Error occurred: ${error}`);
    }
  };

  const onClickHandler =
    (index = 0, isCalled: boolean) =>
    async (
      e: React.MouseEvent<HTMLElement>,
      triggerButton: PhoneButtonTargetTypeEnum | null = null,
      gallery = false
    ) => {
      if (!gallery) {
        e.preventDefault();
        e.persist();
      }

      if (phonesShown) {
        callPhones(phonesShown, index, triggerButton, isCalled);
        return null;
      }
      await fetchData(index, isCalled, triggerButton);
    };

  return {
    onClickHandler,
    phonesShown,
    setPhonesShown,
    setIsVisible,
    isVisible,
    showAlertMessage
  };
};
